import { useQueryClient, useMutation } from '@tanstack/react-query';
import { apiPatch } from '@/lib/api';
import type { Task } from '@/lib/schemas';

// ============================================
// GANTT HOOKS
// ============================================
//
// t17: small helper hooks dedicated to the Gantt view. Mirrors
// use-calendar.ts (date mutations) and use-calendar.ts sprint helpers
// so Calendar and Gantt share the same source-of-truth for sprint
// resolution.
//
//   - updateTaskDates — PATCH /api/tasks/:id with new startDate /
//     endDate values, derived from a drag-to-reschedule gesture.
//     Optimistic write on every ['tasks','list', projectId, ...]
//     cache variant with rollback on failure.
//
//   - Re-exports getSprintBands / getSprintColourForTask so Gantt can
//     render sprint bands the same way Calendar does.

// ============================================
// RE-EXPORTS
// ============================================

export { getSprintBands, getSprintColourForTask } from './use-calendar';
export type { SprintDef, SprintBandsPayload } from './use-calendar';

// ============================================
// TASK-DATE MUTATION
// ============================================
//
// PATCH /api/tasks/:id with both startDate and endDate when the user
// drags a bar to reschedule. Mirrors useUpdateTaskDate in use-calendar
// but always writes both columns so the bar keeps its duration on
// a translate drag; resize drags use the same mutation with both
// dates explicit.
//
// When only `endDate` (or `startDate`) is provided, we send only that
// field so the backend's PATCH handler applies a partial update — no
// auto-date hook runs unless the status changed too.

export interface UpdateTaskDatesInput {
    taskId: string;
    startDate: string | null;
    endDate: string | null;
    projectId: string;
}

export function useUpdateTaskDates(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: UpdateTaskDatesInput) => {
            const payload: Record<string, unknown> = {};
            if (input.startDate !== undefined) {
                payload.startDate = input.startDate;
            }
            if (input.endDate !== undefined) {
                payload.endDate = input.endDate;
            }
            return apiPatch<Task>(`/tasks/${input.taskId}`, payload);
        },
        // Optimistic update + rollback. Same pattern as useUpdateTaskDate
        // — snapshot every ['tasks','list', projectId, ...] cache
        // variant so any in-flight page can roll back independently.
        onMutate: async (input) => {
            const queryKeys = queryClient
                .getQueryCache()
                .findAll({ queryKey: ['tasks', 'list', projectId] })
                .map((q) => q.queryKey);

            const snapshot = new Map<string, unknown>();
            for (const key of queryKeys) {
                snapshot.set(JSON.stringify(key), queryClient.getQueryData(key));
            }

            for (const key of queryKeys) {
                queryClient.setQueryData(key, (old: any) => {
                    if (!old?.pages) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page: any) => ({
                            ...page,
                            tasks: page.tasks.map((t: Task) => {
                                if (t.id !== input.taskId) return t;
                                return {
                                    ...t,
                                    ...(input.startDate !== undefined
                                        ? { startDate: input.startDate }
                                        : {}),
                                    ...(input.endDate !== undefined
                                        ? { endDate: input.endDate }
                                        : {}),
                                };
                            }),
                        })),
                    };
                });
            }

            return { snapshot, queryKeys };
        },
        onError: (_err, _input, context) => {
            if (!context) return;
            for (const [json, data] of context.snapshot) {
                queryClient.setQueryData(JSON.parse(json), data);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
        },
    });
}
